import { Component, OnInit, OnDestroy, Input, OnChanges, SimpleChanges } from '@angular/core';
import { AppManagerService } from './appManager.service';
import { Subject, takeUntil, forkJoin } from 'rxjs';
import { AppManagerModel } from '../interface/app-config-model';
import { showToast } from '../interface/runMessage';
import { DesktopData } from '../header/header.service';
import { AuthService } from '../services/auth.services';

@Component({
  selector: 'app-manager',
  templateUrl: './appManager.component.html',
  styleUrl: './appManager.component.scss',
  standalone: false,
})
export class AppManagerComponent implements OnInit, OnDestroy, OnChanges {
  @Input() desktopView: string = 'desktopOne';
  @Input() desktopNum: number = 1;

  allApps: AppManagerModel[] = [];
  installedAppIds: Set<string> = new Set();
  filteredApps: AppManagerModel[] = [];
  searchQuery: string = '';
  selectedFilter: 'all' | 'installed' | 'available' | 'upcoming' = 'all';
  isLoading: boolean = false;
  isCompactView: boolean = true;
  userId: string = '';
  private readonly REVERSE_DESKTOP_VIEW_MAP = DesktopData();
  private destroy$ = new Subject<void>();



  constructor(
    private appManagerService: AppManagerService,
    public auth: AuthService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['desktopView'] && this.desktopView) {
      this.desktopNum = this.REVERSE_DESKTOP_VIEW_MAP.find((desktop) => desktop.ref === this.desktopView)?.id || 1;
    }
  }

  ngOnInit(): void {
    // Get user from Auth0
    this.auth.user$
      .pipe(takeUntil(this.destroy$))
      .subscribe((profile: any) => {
        const user = profile?.profile ?? profile;
        if (user._id || user?.email) {
          this.userId = user._id ?? user.email;
          this.loadApps();
        }
      });

    // Subscribe to loading state
    this.appManagerService.loading$
      .pipe(takeUntil(this.destroy$))
      .subscribe(loading => {
        this.isLoading = loading;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load all apps and installed apps
   */
  loadApps(): void {
    this.isLoading = true;

    forkJoin({
      allApps: this.appManagerService.getAllApps(),
      installedApps: this.appManagerService.getInstalledApps(this.userId)
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ allApps, installedApps }) => {
          // Create a set of installed app IDs for quick lookup
          this.installedAppIds = new Set(installedApps.map(app => app.appManagerId));

          // Mark apps as installed
          this.allApps = allApps.map(app => ({
            ...app,
            isInstalled: this.installedAppIds.has(app._id.valueOf().toString())
          }));

          this.applyFilters();
          this.isLoading = false;
        },
        error: (error) => {
          showToast('Error loading apps:' + error.message, 'error');
          this.isLoading = false;
        }
      });
  }

  /**
   * Install an app
   */
  installApp(app: AppManagerModel): void {
    if (app.isInstalled) return;

    this.appManagerService.installApp(app._id, this.userId, this.desktopView)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          showToast(response.message, 'success');
          app.isInstalled = true;
          this.installedAppIds.add(app._id);
          this.applyFilters();
        },
        error: (error) => {
          showToast('Error installing app:' + error.message, 'error');
        }
      });
  }

  /**
   * Uninstall an app
   */
  uninstallApp(app: AppManagerModel): void {
    if (!app.isInstalled) return;

    this.appManagerService.uninstallApp(app._id, this.userId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          showToast(response.message, 'success');
          app.isInstalled = false;
          this.installedAppIds.delete(app._id);
          this.applyFilters();
        },
        error: (error) => {
          showToast('Error uninstalling app:' + error.message, 'error');
        }
      });
  }

  /**
   * Handle search input
   */
  onSearchChange(query: string | Event): void {
    const queryString = typeof query === 'string' ? query : (query.target as HTMLInputElement).value;
    this.searchQuery = queryString?.toLowerCase() ?? '';
    this.applyFilters();
  }

  /**
   * Change filter type
   */
  onFilterChange(filter: 'all' | 'installed' | 'available' | 'upcoming'): void {
    this.selectedFilter = filter;
    this.applyFilters();
  }

  /**
   * Apply search and filter
   */
  private applyFilters(): void {
    let filtered = [...this.allApps];

    // Apply status filter
    if (this.selectedFilter === 'installed') {
      filtered = filtered.filter(app => app.isInstalled);
    } else if (this.selectedFilter === 'available') {
      filtered = filtered.filter(app => !app.isInstalled);
    } else if (this.selectedFilter === 'upcoming') {
      filtered = filtered.filter(app => app.upComing);
    }

    // Apply search filter
    if (this.searchQuery) {
      filtered = filtered.filter(app =>
        app.name.toLowerCase().includes(this.searchQuery) ||
        app.description.toLowerCase().includes(this.searchQuery)
      );
    }

    this.filteredApps = filtered;
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.applyFilters();
  }

  toggleView(): void {
    this.isCompactView = !this.isCompactView;
  }

  /**
   * Get installed apps count
   */
  get installedCount(): number {
    return this.allApps.filter(app => app.isInstalled).length;
  }

  /**
   * Get available apps count
   */
  get availableCount(): number {
    return this.allApps.filter(app => !app.isInstalled).length;
  }

  /**
   * Get upcoming apps count
   */
  get upcomingCount(): number {
    return this.allApps.filter(app => app.upComing).length;
  }
}
